import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { ChevronDown, Plus, RefreshCw, X } from "lucide-react";
import {
  DEFAULT_MEMO_TEMPLATE,
  paths,
  QbBillSettingsSchema,
  renderBillMemo,
  SURCHARGE_LABEL,
  SURCHARGE_TYPES,
  type QbBillSettings,
  type QbNewItemType,
  type QbRef,
  type SurchargeHandling,
  type SurchargeType,
} from "@clevis/shared-types";
import { Button, Input, Select, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { useAuth } from "../hooks/useAuth";

// How expenses land in QuickBooks as Bills (skuru's QuickBooks Mapping tab,
// one card): every line as an item on the Items tab, or every line's full
// amount to one account on the Expenses tab (how CSG books them); where
// freight, fuel, tax, adjustments and other fees post and whether they post
// at all; defaults for items Clevis creates; and optional job costing.
// "More options" holds every other rule a bill follows. Nothing is fixed in
// code: each one defaults to what skuru does (docs/qb-expenses-audit.md).

type Target = "desktop" | "online";
interface ListEntry {
  id: string;
  name: string;
  type?: string;
}

const HANDLING: Array<[SurchargeHandling, string]> = [
  ["expense", "Own line on the bill"],
  ["distribute", "Spread into the parts"],
  ["ignore", "Don't post"],
];
const ITEM_TYPES: Array<[QbNewItemType, string]> = [
  ["NonInventory", "Non-inventory"],
  ["Service", "Service"],
  ["OtherCharge", "Other charge"],
  ["Inventory", "Inventory"],
];

function AccountSelect({
  accounts,
  value,
  onChange,
  placeholder = "Not set",
}: {
  accounts: ListEntry[];
  value: QbRef | null;
  onChange: (v: QbRef | null) => void;
  placeholder?: string;
}) {
  const missing = value && !accounts.some((a) => a.id === value.id);
  return (
    <Select
      className={cn("py-1 text-xs", missing && "border-danger")}
      value={value?.id ?? ""}
      onChange={(e) => {
        const a = accounts.find((x) => x.id === e.target.value);
        onChange(a ? { id: a.id, name: a.name } : null);
      }}
    >
      <option value="">{placeholder}</option>
      {missing ? <option value={value!.id}>{value!.name} (not in QuickBooks anymore)</option> : null}
      {accounts.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
          {a.type ? ` · ${a.type}` : ""}
        </option>
      ))}
    </Select>
  );
}

const DISTRIBUTE_LABEL = {
  omit: "left off the bill",
  ownLine: "posted as their own line",
  spread: "spread across the part lines",
} as const;

function Option({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-ink">{label}</p>
      <p className="mb-1 text-[11px] text-steel">{hint}</p>
      {children}
    </div>
  );
}

export function QbBillSettingsCard({ target }: { target: Target }) {
  const { tenantId } = useAuth();
  const slug = target === "online" ? "quickbooks-online" : "quickbooks-desktop";
  const [saved, setSaved] = useState<QbBillSettings | null>(null);
  const [draft, setDraft] = useState<QbBillSettings | null>(null);
  const [accounts, setAccounts] = useState<ListEntry[]>([]);
  const [classes, setClasses] = useState<ListEntry[]>([]);
  const [more, setMore] = useState(false);
  const [newPhrase, setNewPhrase] = useState("");
  const [newPhraseType, setNewPhraseType] = useState<SurchargeType>("shipping");
  const [listNote, setListNote] = useState("");
  const [busy, setBusy] = useState<"save" | "lists" | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenantIntegrationConfig(tenantId, slug)), (snap) => {
      const s = QbBillSettingsSchema.parse((snap.get("billSettings") as unknown) ?? {});
      setSaved(s);
      setDraft((d) => d ?? s);
    });
  }, [tenantId, slug]);

  async function loadLists() {
    if (!tenantId) return;
    try {
      const res = await call<{
        accounts: ListEntry[];
        classes: ListEntry[];
        scannedAt: number | null;
        scanQueued: boolean;
        connected: boolean;
      }>("listQbLists", { tenantId, target }, { mutation: false });
      setAccounts(res.accounts);
      setClasses(res.classes ?? []);
      setListNote(
        target === "desktop"
          ? !res.connected
            ? "Connect QuickBooks Desktop first."
            : res.scannedAt
              ? `Lists read from QuickBooks ${new Date(res.scannedAt).toLocaleString()}.`
              : "Reading your QuickBooks lists on the Web Connector's next check-in (keep QuickBooks open), then refresh."
          : "",
      );
    } catch (err) {
      setListNote((err as Error).message);
    }
  }

  useEffect(() => {
    void loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, target]);

  async function refreshLists() {
    if (!tenantId) return;
    setBusy("lists");
    try {
      const res = await call<{ outcome: string }>("refreshQbdLists", { tenantId });
      setListNote(
        res.outcome === "not_connected"
          ? "Connect QuickBooks Desktop first."
          : "Asked QuickBooks for fresh vendor, item and account lists. They arrive on the Web Connector's next check-in.",
      );
    } catch (err) {
      setListNote((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!tenantId || !draft) return;
    setBusy("save");
    setNote("");
    try {
      const { updatedAt: _u, ...settings } = draft;
      await call("setQbBillSettings", { tenantId, target, settings });
      setNote("Saved. Expenses you send from now on post this way.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (!draft || !saved) return null;
  const set = (patch: Partial<QbBillSettings>) => setDraft({ ...draft, ...patch });
  const dirty = JSON.stringify({ ...draft, updatedAt: 0 }) !== JSON.stringify({ ...saved, updatedAt: 0 });
  const effectiveDistribute = draft.distributeMode ?? (target === "desktop" ? "omit" : "ownLine");
  const modeBtn = (on: boolean) =>
    cn("rounded-md px-3 py-1.5 text-xs font-medium", on ? "bg-ink text-white" : "bg-cream text-steel hover:text-ink");

  return (
    <div className="mt-4 space-y-3 border-t border-steel/15 pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-ink">Expenses → QuickBooks bills</p>
        {target === "desktop" ? (
          <Button variant="ghost" className="px-2 py-1 text-xs" busy={busy === "lists"} onClick={() => void refreshLists()}>
            <RefreshCw size={12} /> Refresh lists
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-steel">
        Supplier invoices, receipts and other expenses post to QuickBooks as vendor Bills.{" "}
        {listNote ? <span className="text-ink">{listNote}</span> : null}
      </p>

      <div>
        <p className="mb-1 text-[11px] font-medium text-steel">How lines post to the bill</p>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" className={modeBtn(draft.billLineMode === "items")} onClick={() => set({ billLineMode: "items" })}>
            Track items on the bill
          </button>
          <button type="button" className={modeBtn(draft.billLineMode === "expenses")} onClick={() => set({ billLineMode: "expenses" })}>
            Bill all lines as expenses
          </button>
        </div>
        <p className="mt-1 text-[11px] text-steel">
          {draft.billLineMode === "expenses"
            ? "Every line posts its full amount to one account on the bill's Expenses tab. No QuickBooks items are matched or created."
            : "Each line is a QuickBooks item on the bill's Items tab: matched by its link, part number or name, or created with the defaults below."}
        </p>
        {draft.billLineMode === "expenses" ? (
          <div className="mt-1.5 grid grid-cols-[9rem_1fr] items-center gap-2">
            <span className="text-xs text-ink">Post every line to</span>
            <AccountSelect accounts={accounts} value={draft.expenseModeAccount} onChange={(v) => set({ expenseModeAccount: v })} placeholder="Pick an account" />
          </div>
        ) : null}
        <div className="mt-1.5 grid grid-cols-[9rem_1fr] items-center gap-2">
          <span className="text-xs text-ink">Referral rewards to</span>
          <AccountSelect accounts={accounts} value={draft.referralRewardsAccount} onChange={(v) => set({ referralRewardsAccount: v })} placeholder={draft.billLineMode === "expenses" ? "Same as every line" : "Pick an account (e.g. Advertising)"} />
        </div>
        <p className="mt-1 text-[11px] text-steel">A referral reward paid out (Clevis Relay) is an expense on the friend's job; it posts here, never as an item.</p>
      </div>

      <div>
        <p className="mb-1 text-[11px] font-medium text-steel">Freight, fuel, tax and other charges</p>
        <div className="space-y-1.5">
          {SURCHARGE_TYPES.map((t) => (
            <div key={t} className="grid grid-cols-[9rem_10rem_1fr] items-center gap-2">
              <span className="text-xs text-ink">{SURCHARGE_LABEL[t]}</span>
              <Select
                className="py-1 text-xs"
                value={draft.surchargeHandling[t]}
                onChange={(e) => set({ surchargeHandling: { ...draft.surchargeHandling, [t]: e.target.value as SurchargeHandling } })}
              >
                {HANDLING.map(([h, label]) => (
                  <option key={h} value={h}>
                    {label}
                  </option>
                ))}
              </Select>
              {draft.surchargeHandling[t] === "ignore" ? (
                <span className="text-[11px] text-steel">Never posted.</span>
              ) : (
                <AccountSelect
                  accounts={accounts}
                  value={draft.surchargeAccounts[t]}
                  onChange={(v) => set({ surchargeAccounts: { ...draft.surchargeAccounts, [t]: v } })}
                />
              )}
            </div>
          ))}
        </div>
        {SURCHARGE_TYPES.some((t) => draft.surchargeHandling[t] === "distribute") ? (
          <p className="mt-1 text-[11px] text-steel">
            “Spread into the parts” charges are {DISTRIBUTE_LABEL[effectiveDistribute]} (More options).
          </p>
        ) : null}
        {draft.rerouteFeeLines ? (
          <p className="mt-1 text-[11px] text-steel">
            A line that's only freight or fuel (“FREIGHT”, “Fuel surcharge”) posts like these charges, to the account above.
          </p>
        ) : null}
      </div>

      {draft.billLineMode === "items" ? (
        <div>
          <p className="mb-1 text-[11px] font-medium text-steel">When a line isn't a QuickBooks item yet, create it as</p>
          <div className="grid grid-cols-[9rem_1fr] items-center gap-2">
            <span className="text-xs text-ink">Item type</span>
            <Select
              className="py-1 text-xs"
              value={draft.newItemDefaults.type}
              onChange={(e) => set({ newItemDefaults: { ...draft.newItemDefaults, type: e.target.value as QbNewItemType } })}
            >
              {ITEM_TYPES.filter(([t]) => target === "desktop" || t !== "OtherCharge").map(([t, label]) => (
                <option key={t} value={t}>
                  {label}
                </option>
              ))}
            </Select>
            <span className="text-xs text-ink">Income account</span>
            <AccountSelect
              accounts={accounts}
              value={draft.newItemDefaults.incomeAccount}
              onChange={(v) => set({ newItemDefaults: { ...draft.newItemDefaults, incomeAccount: v } })}
            />
            <span className="text-xs text-ink">Expense / COGS account</span>
            <AccountSelect
              accounts={accounts}
              value={draft.newItemDefaults.expenseAccount}
              onChange={(v) => set({ newItemDefaults: { ...draft.newItemDefaults, expenseAccount: v } })}
            />
            {draft.newItemDefaults.type === "Inventory" ? (
              <>
                <span className="text-xs text-ink">Inventory asset account</span>
                <AccountSelect
                  accounts={accounts}
                  value={draft.newItemDefaults.assetAccount}
                  onChange={(v) => set({ newItemDefaults: { ...draft.newItemDefaults, assetAccount: v } })}
                />
              </>
            ) : null}
          </div>
          {draft.newItemDefaults.type === "Inventory" && target === "desktop" ? (
            <p className="mt-1 text-[11px] text-steel">Inventory items need inventory turned on in QuickBooks (Edit → Preferences → Items &amp; Inventory).</p>
          ) : null}
        </div>
      ) : null}

      <label className="flex cursor-pointer items-start gap-2 text-xs text-ink">
        <input type="checkbox" className="mt-0.5" checked={draft.jobCosting} onChange={(e) => set({ jobCosting: e.target.checked })} />
        <span>
          Tag each bill line with the job's customer (QuickBooks job costing).
          <span className="block text-[11px] text-steel">
            Clevis shows each job's costs either way. Turn this on to see them in QuickBooks' job reports too. Default: off.
          </span>
        </span>
      </label>
      {draft.jobCosting ? (
        <label className="ml-5 flex cursor-pointer items-start gap-2 text-xs text-ink">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={draft.jobCostingBillable}
            onChange={(e) => set({ jobCostingBillable: e.target.checked })}
          />
          <span>
            Mark those lines billable to the customer
            <span className="block text-[11px] text-steel">
              For costs you re-bill (QuickBooks then offers them when you invoice that customer). Off: not billable.
            </span>
          </span>
        </label>
      ) : null}

      <div className="rounded-lg border border-steel/15">
        <button
          type="button"
          className="flex w-full items-center justify-between px-2.5 py-1.5 text-left text-xs font-medium text-ink"
          onClick={() => setMore((m) => !m)}
        >
          More options
          <ChevronDown size={14} className={cn("text-steel transition-transform", more && "rotate-180")} />
        </button>
        {more ? (
          <div className="space-y-3 border-t border-steel/15 px-2.5 py-2.5">
            <Option
              label="“Spread into the parts” charges"
              hint={`Default for ${target === "desktop" ? "QuickBooks Desktop" : "QuickBooks Online"}: ${DISTRIBUTE_LABEL[target === "desktop" ? "omit" : "ownLine"]}.`}
            >
              <Select
                className="py-1 text-xs"
                value={draft.distributeMode ?? ""}
                onChange={(e) => set({ distributeMode: (e.target.value || null) as QbBillSettings["distributeMode"] })}
              >
                <option value="">Default ({DISTRIBUTE_LABEL[target === "desktop" ? "omit" : "ownLine"]})</option>
                <option value="omit">{DISTRIBUTE_LABEL.omit}</option>
                <option value="ownLine">{DISTRIBUTE_LABEL.ownLine}</option>
                <option value="spread">{DISTRIBUTE_LABEL.spread}</option>
              </Select>
            </Option>

            <Option label="Lines that are only a fee" hint="“FREIGHT”, “Delivery”, “Fuel surcharge”… Default: on.">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-ink">
                <input type="checkbox" checked={draft.rerouteFeeLines} onChange={(e) => set({ rerouteFeeLines: e.target.checked })} />
                Post them to the freight / fuel accounts instead of as parts
              </label>
              {draft.rerouteFeeLines ? (
                <div className="mt-1.5 space-y-1">
                  {draft.extraFeeLines.map((x, i) => (
                    <div key={`${x.phrase}${i}`} className="flex items-center gap-1.5 text-xs text-ink">
                      <span className="min-w-0 flex-1 truncate">“{x.phrase}” → {SURCHARGE_LABEL[x.type]}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${x.phrase}`}
                        className="text-steel hover:text-danger"
                        onClick={() => set({ extraFeeLines: draft.extraFeeLines.filter((_, j) => j !== i) })}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5">
                    <Input
                      className="py-1 text-xs"
                      placeholder="Your own, e.g. “Pallet charge”"
                      value={newPhrase}
                      onChange={(e) => setNewPhrase(e.target.value)}
                    />
                    <Select className="w-36 py-1 text-xs" value={newPhraseType} onChange={(e) => setNewPhraseType(e.target.value as SurchargeType)}>
                      {SURCHARGE_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {SURCHARGE_LABEL[t]}
                        </option>
                      ))}
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      disabled={newPhrase.trim().length < 2 || draft.extraFeeLines.length >= 30}
                      onClick={() => {
                        set({ extraFeeLines: [...draft.extraFeeLines, { phrase: newPhrase.trim(), type: newPhraseType }] });
                        setNewPhrase("");
                      }}
                    >
                      <Plus size={12} /> Add
                    </Button>
                  </div>
                  <p className="text-[11px] text-steel">Matches the whole line text, ignoring case and punctuation.</p>
                </div>
              ) : null}
            </Option>

            <Option
              label="Supplier invoice numbers that are too long"
              hint={`QuickBooks ${target === "desktop" ? "Desktop allows 11" : "Online allows 21"} characters. Default: keep the first ones.`}
            >
              <Select
                className="py-1 text-xs"
                value={draft.refNumberOverflow}
                onChange={(e) => set({ refNumberOverflow: e.target.value as QbBillSettings["refNumberOverflow"] })}
              >
                <option value="first">Keep the first characters</option>
                <option value="last">Keep the last characters</option>
                <option value="omit">Leave it blank (QuickBooks numbers the bill)</option>
              </Select>
            </Option>

            <Option label="Bill memo" hint="Tokens: {expense} {job} {vendor} {invoice}. Empty pieces drop out with their dash.">
              <Input
                className="py-1 text-xs"
                value={draft.memoTemplate}
                placeholder={DEFAULT_MEMO_TEMPLATE}
                onChange={(e) => set({ memoTemplate: e.target.value.slice(0, 200) })}
              />
              <p className="mt-0.5 text-[11px] text-steel">
                Looks like: “{renderBillMemo(draft.memoTemplate, { expense: 1042, job: "Lot 12 Crestline", vendor: "ABC Supply", invoice: "INV-7781" })}”
              </p>
            </Option>

            <Option label="Vendors not in QuickBooks yet" hint="Default: create them.">
              <Select
                className="py-1 text-xs"
                value={draft.newVendors}
                onChange={(e) => set({ newVendors: e.target.value as QbBillSettings["newVendors"] })}
              >
                <option value="create">Create the vendor in QuickBooks</option>
                <option value="pick">Make me pick one before sending</option>
              </Select>
            </Option>

            <Option label="Class on bill lines" hint="For QuickBooks class tracking. Default: none.">
              <Select
                className="py-1 text-xs"
                value={draft.billClass?.id ?? ""}
                onChange={(e) => {
                  const c = classes.find((x) => x.id === e.target.value);
                  set({ billClass: c ? { id: c.id, name: c.name } : null });
                }}
              >
                <option value="">No class</option>
                {draft.billClass && !classes.some((c) => c.id === draft.billClass!.id) ? (
                  <option value={draft.billClass.id}>{draft.billClass.name} (not in QuickBooks anymore)</option>
                ) : null}
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              {classes.length === 0 ? <p className="mt-0.5 text-[11px] text-steel">No classes in your QuickBooks (or class tracking is off).</p> : null}
            </Option>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button className="px-3 py-1 text-xs" busy={busy === "save"} disabled={!dirty} onClick={() => void save()}>
          Save bill settings
        </Button>
        {dirty ? (
          <button className="text-xs text-steel underline hover:text-ink" onClick={() => setDraft(saved)}>
            Undo changes
          </button>
        ) : null}
        {note ? <span className="text-xs text-ink">{note}</span> : null}
      </div>
    </div>
  );
}
